// Every file kept against an RFQ: drop in any number, tag each one, tie it to a
// line. Nothing is ever deleted — "Remove" hides a file with a reason, and the
// removed list stays one click away for an audit.
//
// Layout: one card per file, not a table. File names are long ("Aero Products
// Component Services, Inc. quote vq_01M5K19…pdf") and a table cell cannot shrink
// around an unbreakable name, so on a phone it ran over its neighbours and cut
// off the columns. Here the name wraps, the facts sit under it, and the controls
// get their own row.

import { useId, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Archive, ChevronRight, File as FileIcon, FileArchive, FileSpreadsheet, FileText, Film, FolderOpen, Image as ImageIcon, Mail, Upload,
} from 'lucide-react';
import { removeRfqDocument, updateRfqDocument, type RfqDocument, type RfqDocumentKind } from '@/lib/api';
import {
  CERT_KINDS, RFQ_DOCUMENT_ACCEPT, RFQ_DOCUMENT_KINDS, RFQ_DOCUMENT_KIND_LABELS, RFQ_DOCUMENT_MAX_BYTES,
  fileGlyph, guessRfqDocumentKind, summarizeRfqFiles, type FileGlyph,
} from '@shared/rfq-documents';
import { errorMessage, formatBytes } from '@/lib/rfq-files';
import {
  RfqFileLink, UploadQueueList, rfqDocumentsKey, useRfqDocuments, useRfqUploadQueue,
} from './rfq-files-shared';

interface LineOption { id: string; lineNumber: number; partNumber: string }

const LEGACY_LABEL: Record<NonNullable<RfqDocument['legacy']>['source'], string> = {
  line_cert: 'Line cert',
  replaced_line_cert: 'Replaced cert',
  customer_po: 'PO card',
  replaced_customer_po: 'Replaced PO',
  vendor_quote_pdf: 'Quote parser',
};

const GLYPHS: Record<FileGlyph, typeof FileIcon> = {
  pdf: FileText, image: ImageIcon, sheet: FileSpreadsheet, mail: Mail, archive: FileArchive, video: Film,
  doc: FileText, file: FileIcon,
};

const dateLabel = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

// 16px text on a phone: iOS Safari zooms the whole page when a control under 16px is focused.
// Collapsed unless the person opened it; the choice sticks across RFQs. Per-browser only.
const OPEN_KEY = 'apd.rfq-files-open';
function readOpen(): boolean {
  try { return localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; }
}
function writeOpen(open: boolean): void {
  try { localStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch { /* private window: the choice just does not stick */ }
}

const CONTROL = 'h-9 sm:h-8 min-w-0 max-w-full rounded-md border border-border bg-background px-2 text-base sm:text-xs';
const CHIP = 'inline-flex items-center rounded-md px-2 py-1 text-xs';

export function RfqFilesPanel({ rfqPublicId, lines }: { rfqPublicId: string; lines: LineOption[] }) {
  const { data, isLoading, isError, error } = useRfqDocuments(rfqPublicId);
  const { queue, add, dismiss } = useRfqUploadQueue(rfqPublicId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [open, setOpen] = useState(readOpen);
  const bodyId = useId();

  const all = data?.documents ?? [];
  const { certs } = summarizeRfqFiles(all);
  const current = all.filter((d) => !d.removedAt);
  const removed = all.filter((d) => d.removedAt);
  const onlyLine = lines.length === 1 ? lines[0]!.id : null;

  function addFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (!list.length) return;
    add(list.map((file) => {
      const kind = guessRfqDocumentKind(file.name);
      // A cert on a one-line RFQ can only belong to that line.
      return { file, kind, lineItemId: CERT_KINDS.has(kind) ? onlyLine : null };
    }));
  }

  return (
    <div
      className={`mt-4 rounded-md border bg-surface ${dragging ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false); }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
    >
      {/* The header is always visible. Adding files never needs the section open, and
          a drop anywhere on the panel (collapsed or not) uploads. */}
      <div className="flex items-center gap-2 pr-3">
        <button
          type="button"
          onClick={() => { const next = !open; setOpen(next); writeOpen(next); }}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md px-3 text-left hover:bg-muted/60 sm:min-h-10"
        >
          <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Files ({current.length})</span>
          {isLoading ? (
            <span className="truncate text-xs text-muted-foreground">Loading…</span>
          ) : certs > 0 ? (
            <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-800">
              {certs} {certs === 1 ? 'cert' : 'certs'}
            </span>
          ) : current.length === 0 ? (
            <span className="truncate text-xs text-muted-foreground">No files yet</span>
          ) : null}
        </button>
        <button type="button" onClick={() => inputRef.current?.click()}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium hover:bg-muted sm:h-8">
          <Upload className="h-3.5 w-3.5" aria-hidden /> Add files
        </button>
      </div>

      {/* Progress and failures stay visible even when the section is closed. */}
      {(queue.length > 0 || isError) && (
        <div className="space-y-2 border-t border-border px-4 py-3">
          <UploadQueueList queue={queue} onDismiss={dismiss} />
          {isError && <div className="break-words text-xs text-destructive">{errorMessage(error)}</div>}
        </div>
      )}

      {open && (
        <div id={bodyId} className="border-t border-border">
          {!isLoading && !current.length && !queue.length && (
            <div className="px-4 py-3">
              <button type="button" onClick={() => inputRef.current?.click()}
                className="w-full rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground hover:bg-muted">
                <span className="block">Drop quotes, certs, POs, photos or emails here, or tap to choose.</span>
                <span className="mt-1 block">Up to {RFQ_DOCUMENT_MAX_BYTES / 1024 / 1024} MB each, kept exactly as received.</span>
              </button>
            </div>
          )}

          {current.length > 0 && (
            <ul className="divide-y divide-border">
              {current.map((d, i) => (
                <FileRow key={d.id ?? `${d.legacy?.objectKey}-${i}`} rfqPublicId={rfqPublicId} doc={d} lines={lines} />
              ))}
            </ul>
          )}

          {current.length > 0 && (
            <p className="hidden border-t border-border px-4 py-2 text-[11px] text-muted-foreground sm:block">
              Drag more files anywhere onto this panel.
            </p>
          )}

          {removed.length > 0 && (
            <div className="border-t border-border px-4 py-2.5 text-xs">
              <button type="button" onClick={() => setShowRemoved((v) => !v)}
                className="-mx-2 inline-flex h-8 items-center rounded px-2 text-muted-foreground hover:bg-muted hover:text-foreground">
                {showRemoved ? 'Hide' : 'Show'} {removed.length} removed file{removed.length === 1 ? '' : 's'}
              </button>
              {showRemoved && (
                <ul className="mt-1.5 space-y-2">
                  {removed.map((d) => (
                    <li key={d.id} className="min-w-0 text-muted-foreground">
                      <RfqFileLink rfqPublicId={rfqPublicId} doc={d}
                        className="break-words text-muted-foreground line-through hover:underline [overflow-wrap:anywhere]" />
                      <span className="block">Removed {dateLabel(d.removedAt)}: {d.removalReason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <input ref={inputRef} type="file" multiple accept={RFQ_DOCUMENT_ACCEPT} className="hidden"
        onChange={(e) => { const f = e.target.files; if (f) addFiles(f); e.target.value = ''; }} />
    </div>
  );
}

function FileRow({ rfqPublicId, doc, lines }: { rfqPublicId: string; doc: RfqDocument; lines: LineOption[] }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: rfqDocumentsKey(rfqPublicId) });

  const update = useMutation({
    mutationFn: (patch: { kind?: RfqDocumentKind; lineItemId?: string | null }) =>
      updateRfqDocument(rfqPublicId, doc.id!, patch, () => getToken()),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: () => removeRfqDocument(rfqPublicId, doc.id!, reason.trim(), () => getToken()),
    onSuccess: () => { setRemoving(false); refresh(); },
  });
  const editable = !!doc.id;
  const err = update.isError ? errorMessage(update.error) : remove.isError ? errorMessage(remove.error) : null;
  const Glyph = GLYPHS[fileGlyph(doc.filename)];
  const isCert = CERT_KINDS.has(doc.kind);

  const facts = [
    formatBytes(doc.sizeBytes) !== '—' ? formatBytes(doc.sizeBytes) : null,
    dateLabel(doc.uploadedAt),
    doc.uploadedBy,
  ].filter(Boolean) as string[];

  return (
    <li className="px-4 py-3">
      {/* Name row: icon, name + facts, remove. The controls below span the full card. */}
      <div className="flex gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Glyph className="h-[18px] w-[18px]" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <RfqFileLink rfqPublicId={rfqPublicId} doc={doc}
            className="line-clamp-2 break-words text-sm font-medium text-primary hover:underline [overflow-wrap:anywhere]" />
          {facts.length > 0 && (
            <p className="mt-0.5 break-words text-xs text-muted-foreground">{facts.join(' · ')}</p>
          )}
          {doc.legacy && doc.note && <p className="mt-0.5 break-words text-xs text-muted-foreground">{doc.note}</p>}
        </div>

        {/* Same width whether or not the file can be removed, so names wrap alike in every row. */}
        {editable && !removing ? (
          <button type="button" onClick={() => setRemoving(true)} aria-label={`Remove ${doc.filename}`}
            title="Remove (hidden, kept for audit)"
            className="-mr-1.5 -mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-destructive">
            <Archive className="h-4 w-4" aria-hidden />
          </button>
        ) : (
          <span className="-mr-1.5 h-9 w-9 shrink-0" aria-hidden />
        )}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:pl-12">
        {editable ? (
          <>
            <select aria-label="File type" value={doc.kind} disabled={update.isPending}
              onChange={(e) => update.mutate({ kind: e.target.value as RfqDocumentKind })} className={CONTROL}>
              {RFQ_DOCUMENT_KINDS.map((k) => <option key={k} value={k}>{RFQ_DOCUMENT_KIND_LABELS[k]}</option>)}
            </select>
            <select aria-label="Line" value={doc.lineItemId ?? ''} disabled={update.isPending}
              onChange={(e) => update.mutate({ lineItemId: e.target.value || null })} className={`${CONTROL} sm:max-w-[11rem]`}>
              <option value="">Whole RFQ</option>
              {lines.map((l) => <option key={l.id} value={l.id}>#{l.lineNumber} {l.partNumber}</option>)}
            </select>
          </>
        ) : (
          <div className="col-span-2 flex flex-wrap items-center gap-2 sm:col-span-1">
            <span className={`${CHIP} ${isCert ? 'bg-emerald-50 text-emerald-800' : 'bg-muted text-foreground'}`}>
              {RFQ_DOCUMENT_KIND_LABELS[doc.kind]}
            </span>
            {doc.lineNumber != null && <span className={`${CHIP} bg-muted text-foreground`}>Line #{doc.lineNumber}</span>}
            {doc.legacy && (
              <span className={`${CHIP} border border-border text-muted-foreground`}
                title="Uploaded before the Files panel existed">
                {LEGACY_LABEL[doc.legacy.source]}
              </span>
            )}
          </div>
        )}
      </div>

      {removing && (
        <form className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:pl-12"
          onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 3) remove.mutate(); }}>
          <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
            placeholder="Why remove it? (kept for audit, just hidden)"
            className="h-9 min-w-0 flex-1 rounded-md border border-border px-2 text-base sm:h-8 sm:text-xs" />
          <div className="flex items-center gap-2">
            <button type="submit" disabled={reason.trim().length < 3 || remove.isPending}
              className="inline-flex h-9 items-center rounded-md border border-border px-3 text-xs font-medium hover:bg-muted disabled:opacity-50 sm:h-8">
              {remove.isPending ? 'Removing…' : 'Remove file'}
            </button>
            <button type="button" onClick={() => setRemoving(false)}
              className="inline-flex h-9 items-center rounded-md px-2 text-xs text-muted-foreground hover:bg-muted sm:h-8">
              Cancel
            </button>
          </div>
        </form>
      )}
      {err && <p className="mt-1 break-words text-xs text-destructive sm:pl-12">{err}</p>}
    </li>
  );
}
